import { useAtomValue } from "jotai";
import first from "lodash/first";
import type { PageProps } from "next";
import { useEffect, useMemo } from "react";
import { graphql, usePreloadedQuery } from "react-relay";
import type { RelayComponent } from "relay-nextjs";
import { withRelay } from "relay-nextjs";
import { findFirstNonEmptyString } from "scmp-sdk";

import { useCanonicalUrl } from "shared/hooks";
import { parseQueryString } from "shared/lib/utils";

import { useTopBannerAdSlot } from "scmp-app/components/advertisement/ad-slots/top-banner-ad-slot/hooks";
import { TargetingPaidType } from "scmp-app/components/advertisement/ad-slots/types";
import { FallbackLoading } from "scmp-app/components/fallback-loading";
import { PageSeo } from "scmp-app/components/meta";
import { trackingAtom } from "scmp-app/components/tracking";
import { createClientEnvironment } from "scmp-app/lib/relay/environment.client";
import { pathname } from "scmp-app/pages/video";
import VideoSeoImage from "scmp-app/pages/video/video-seo-image.png";
import type { videoSectionPageQuery } from "scmp-app/queries/__generated__/videoSectionPageQuery.graphql";

import {
  BannerImage,
  BannerImageContainer,
  Container,
  Header,
  SectionName,
  SectionNameContainer,
  SectionNameHeader,
  StyledVideoHeaderDropdownMenu,
  StyledVideoLatestVideo,
  StyledVideoList,
  StyledVideoShortCarousel,
} from "./styles";

const query = graphql`
  query videoSectionPageQuery(
    $after: String
    $count: Int = 24
    $entityId: String!
    $sectionIds: [String]
    $videoLatestQueueName: String!
    $videoShortQueueName: String!
  ) {
    videoSection(filter: { entityId: $entityId }) {
      entityId
      name
      urlAlias
      advertZone(version: 2)
      images {
        height
        width
        url
      }
    }
    ...videoHeaderDropdownMenuQuery @arguments(withHighlight: false)
    ...latestVideoQuery @arguments(videoLatestQueueName: $videoLatestQueueName)
    ...videoListQuery @arguments(after: $after, count: $count, sectionIds: $sectionIds)
    ...videoShortCarousel @arguments(queueName: $videoShortQueueName)
  }
`;

const VideoSectionPage: RelayComponent<unknown, videoSectionPageQuery> = ({ preloadedQuery }) => {
  const data = usePreloadedQuery(query, preloadedQuery);
  const adZone = useMemo(
    () => findFirstNonEmptyString(data.videoSection.advertZone) ?? "default",
    [data.videoSection.advertZone],
  );

  const bannerImage = useMemo(
    () => first(data.videoSection.images) ?? undefined,
    [data.videoSection.images],
  );

  const bannerImageAspectRatio = useMemo(
    () => `${(bannerImage?.width ?? 0) / (bannerImage?.height ?? 1)}`,
    [bannerImage],
  );

  const { canonicalUrl: videoSectionCanonicalUrl } = useCanonicalUrl(
    data.videoSection.urlAlias ?? undefined,
  );

  const { trackPageView } = useAtomValue(trackingAtom);
  useEffect(() => {
    trackPageView?.();
  }, [trackPageView]);

  useTopBannerAdSlot({
    desktop: {
      adUnit: "d_banner1",
      sizes: [
        [970, 250],
        [728, 90],
        [970, 90],
      ],
      targeting: {
        paid: TargetingPaidType.NotArticle,
      },
      zone: adZone,
    },
    mobile: {
      adUnit: "m_banner3",
      sizes: [
        [300, 100],
        [320, 100],
        [300, 50],
        [320, 50],
      ],
      targeting: {
        paid: TargetingPaidType.NotArticle,
      },
      zone: adZone,
    },
  });

  return (
    <>
      <PageSeo
        canonical={videoSectionCanonicalUrl}
        openGraph={{
          images: [{ url: VideoSeoImage.src }],
          url: videoSectionCanonicalUrl,
        }}
        title={data.videoSection.name}
      />
      <Container>
        <Header>
          <BannerImageContainer>
            {bannerImage && (
              <BannerImage
                $aspectRatio={bannerImageAspectRatio}
                $background="transparent"
                lazyload={false}
                src={bannerImage.url ?? ""}
              />
            )}
          </BannerImageContainer>
          <SectionNameHeader pathname={pathname}>Video /</SectionNameHeader>
          <SectionNameContainer>
            <SectionName>{data.videoSection.name}</SectionName>
            <StyledVideoHeaderDropdownMenu
              activeEntityId={data.videoSection.entityId}
              reference={data}
            />
          </SectionNameContainer>
        </Header>
        <StyledVideoLatestVideo
          adZone={adZone}
          ga4Position="video_section"
          reference={data}
          showTopPicks
        />
        <StyledVideoShortCarousel reference={data} title={`${data.videoSection.name} SHORTS`} />
        <StyledVideoList data={data} enableFilter={false} />
      </Container>
    </>
  );
};

VideoSectionPage.displayName = "VideoSectionPage";

const pageProps: PageProps = {
  appBarConfiguration: {
    adSlotVariants: {
      large: {
        height: 250,
        padding: 20,
      },
      small: {
        height: 100,
        padding: 10,
      },
    },
    hasDesktopAd: true,
    hasMobileAd: true,
    variant: "scmp/slim",
  },
  contentConfiguration: {
    responsivePadding: {
      desktopUp: 12,
      tabletUp: 0,
    },
  },
  pageConfiguration: {
    responsivePadding: {
      tabletUp: 24,
    },
  },
};

export default withRelay(VideoSectionPage, query, {
  clientSideProps: () => pageProps,
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("scmp-app/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  serverSideProps: () => Promise.resolve(pageProps),
  variablesFromContext(context) {
    const entityId = parseQueryString(context.query.entityId) ?? "";
    const entityUuid = parseQueryString(context.query.entityUuid) ?? "";

    return {
      entityId,
      relatedTopicsQueueName: `related_topics_${entityId}`,
      sectionIds: entityUuid ? [entityUuid] : undefined,
      videoLatestQueueName: `video_section_picks_${entityId}`,
      videoShortQueueName: `video_section_shorts_${entityId}`,
    };
  },
});
